import GridTable from '@/components/table/GridTable'
import { HStack } from '@musinsa/mwms-ds'
import { ICellRendererParams, ColDef, ITooltipParams } from 'ag-grid-community'
import Text from '@/components/content/Text'
import Button from '@/components/content/Button'
import { Fragment, useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { colorSystem } from '@/styles/theme'
import { GridTableProps } from '@/components/table/grid_types'
import dayjs from 'dayjs'
import { GRID_SIZE } from '@/utils/grid/grid'
import { useCheckedNum } from '@/components/table/GridTable.atoms'
import styled from 'styled-components'
import useModal from '@/hooks/useModal'
import PromotionFormModal from './PromotionFormModal'
import PromotionBulkUploadModal from './PromotionBulkUploadModal'
import { SettlementPromotionSchemaType, toPromotionKind, toPromotionKindListLabel } from '../schemas/postPromotionSchemas'
import { toDiscountDisplay, toTierLines, toTierSummaryText, toTierTooltipText } from '@/services/settlement/settlementPromotion/discountValue'
import Badge from '@/components/content/Badge'
import { TooltipContent } from '@/components/content/Tooltip'
import { ConfirmModal } from '@/components/content/ConfirmModal'
import {
  PostPromotionListResDisplayType,
  usePatchStopPromotion,
  usePromotionBulkTemplate,
} from '@/services/settlement/settlementPromotion/useSettlementPromotionQueries'
import { formatChargeItemLabel, INBOUND_SETTLEMENT_UNIT_TYPE_FLAG } from '@/services/settlement/settlementPromotion/serviceChargeItem'
import { useFeatureFlag } from '@/services/feature/useFeatureQueries'
import { PromotionCountry, PromotionOrderType } from '@/services/settlement/settlementPromotion/types'
import { useGetLogisticsCategories } from '@/services/commonCode/useCommonCodeQueries'
import { createCategoryLabeler } from '@/services/settlement/settlementPromotion/logisticsCategoryLabel'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'
import { DATE_FORMAT } from '@/utils/consts'
import { tokens } from '@musinsa/layer-tokens/desktop'

// LOGISPRD-11390 — 기간 프로모션 구간 툴팁
function PromotionTierTooltip({ data }: ITooltipParams<PostPromotionListResDisplayType>) {
  const lines = toTierLines(data?.tiers ?? [], data?.discountType?.code === 'FIXED')
  if (lines.length === 0) return null

  return (
    // TooltipContent 의 기본 오프셋은 한 줄 높이(34px) 기준이라 여러 줄이면 위로 자라 셀을 덮는다 — 셀 아래로 고정한다.
    // absolute 를 relative 로 낮춰야 ag-grid 팝업 래퍼가 높이를 갖는다. absolute 면 래퍼 offsetHeight 가 0 이라
    // 경계 보정(PopupService.keepXYWithinBounds)이 0 으로 계산돼, 구간이 많은 아래쪽 행에서 툴팁이 그리드 밖으로 잘린다
    <TooltipContent $isVisible $type="bottom" $whiteSpace="nowrap" style={{ position: 'relative', bottom: 'unset', top: 10 }}>
      <TierTooltipGrid>
        {lines.map((line) => (
          <Fragment key={line.label}>
            <span>{line.label}</span>
            <span>{line.range}</span>
            <TierTooltipDiscount>{line.discount}</TierTooltipDiscount>
          </Fragment>
        ))}
      </TierTooltipGrid>
    </TooltipContent>
  )
}

// 구간·수량·할인값 세 칸을 줄마다 같은 자리에 세운다 (셀 안에서 flex 로 맞추면 줄마다 폭이 달라진다)
const TierTooltipGrid = styled.span`
  display: grid;
  grid-template-columns: auto auto auto;
  column-gap: 12px;
  row-gap: 2px;
  text-align: left;
`

const TierTooltipDiscount = styled.span`
  text-align: right;
  font-weight: 700;
`

type PromotionManagementTableProps = GridTableProps<PostPromotionListResDisplayType> & {
  rowData: PostPromotionListResDisplayType[]
  pageInfo: PageInfoType
  onChangePage: (page: number) => void
  refetch: () => void
}

export function PromotionManagementTable({ isLoading, rowData, pageInfo, onChangePage, refetch }: PromotionManagementTableProps) {
  const $grid = useRef<AgGridReact>(null)

  // 팝업 기본 부모는 .ag-root-wrapper 라 구간 툴팁이 표 안에 갇혀 아래쪽 행에서 행·가로 스크롤바에 겹친다.
  // 렌더마다 새 객체를 넘기면 AG Grid 가 설정을 다시 태우므로 참조를 고정한다
  const gridOptions = useMemo(() => ({ popupParent: document.body }), [])

  const [checkedNum] = useCheckedNum()

  const promotionModal = useModal()
  const bulkUploadModal = useModal()
  const terminatePromotionModal = useModal()

  const patchStopPromotion = usePatchStopPromotion()

  // LP8658: 박스단위 입고 정산 feature flag. OFF 면 서비스 항목 컬럼을 레거시 name 그대로 표시 (fail-safe 기본 false)
  const { data: isInboundSettlementUnitTypeEnabled } = useFeatureFlag(INBOUND_SETTLEMENT_UNIT_TYPE_FLAG)
  const { onDownloadTemplate } = usePromotionBulkTemplate()
  // 카테고리 컬럼을 등록 모달·검색 필터와 같은 '카테고리명 (코드)' 로 표기한다
  const { data: logisticsCategories } = useGetLogisticsCategories()
  const categoryLabelOf = useMemo(() => createCategoryLabeler(logisticsCategories), [logisticsCategories])

  const [selectedRowForEdit, setSelectedRowForEdit] = useState<(SettlementPromotionSchemaType & { promotionId?: number }) | null>(null)

  const handleRegisterModalClose = (closeCb?: () => void) => {
    if (closeCb) {
      closeCb()
    }

    setSelectedRowForEdit(null)
    promotionModal.onCloseModal()
  }

  const handleTerminatePromotion = async () => {
    try {
      await patchStopPromotion.mutateAsync({
        promotionIds: $grid.current?.api.getSelectedRows().map((row) => row.id) ?? [],
      })

      await refetch()

      $grid.current?.api.deselectAll()
      terminatePromotionModal.onCloseModal()
    } catch (error) {
      MAlert.show(Utils.getApiErrMessage(error, '프로모션 종료를 할 수 없습니다. 개발팀에 문의해주세요.'))
    }
  }

  // LP8658: flag 의존(width/valueFormatter)이 추가되어, flag 로드 전후 불필요한 컬럼 재정의를 막기 위해 메모이즈. 핸들러(setSelectedRowForEdit/onOpenModal)는 stable
  const columnDefs = useMemo<ColDef<PostPromotionListResDisplayType>[]>(
    () => [
      {
        // LOGISPRD-10268 — 국가 축 (LOGISPRD-11315 이전 배포분은 미전송 = '-')
        field: 'country',
        headerName: '국가',
        width: 70,
        valueFormatter: ({ value }) => value?.name ?? '-',
      },
      {
        // LOGISPRD-11315 — 주문유형 축 (한국은 국내·해외(GPC) 두 갈래)
        field: 'orderType',
        headerName: '주문유형',
        width: 80,
        valueFormatter: ({ value }) => value?.name ?? '-',
      },
      {
        field: 'serviceType',
        headerName: '서비스 항목',
        width: isInboundSettlementUnitTypeEnabled ? 130 : 100,
        // LP8658: flag ON 이면 INBOUND 을 unitType 에 따라 "입고비 PCS단가"/"입고비 BOX단가"(미배포 구간 PCS 가드), OFF 면 레거시 name 그대로
        valueFormatter: ({ value, data }) =>
          isInboundSettlementUnitTypeEnabled ? formatChargeItemLabel(value.code, value.name, data?.unitType ?? 'PCS') : value.name,
      },
      {
        field: 'title',
        headerName: '프로모션명',
        width: 175,
      },
      {
        // 시안 v4 — 프로모션 종류 (저장 축에서 파생, 서버 응답 컬럼이 아니라 colId 로 둔다)
        colId: 'promotionKind',
        headerName: '프로모션 종류',
        width: 120,
        valueGetter: ({ data }) =>
          data
            ? toPromotionKindListLabel({
                targetUnit: data.targetUnit,
                partnerIds: (data.partnerIds ?? [])[0] ?? '',
                brandIds: (data.brands ?? []).map((brand) => brand.brandId),
                categoryCodes: data.categoryCodes ?? [],
              })
            : '',
      },
      {
        field: 'partnerIds',
        headerName: '업체아이디',
        width: 130,
        // LOGISPRD-10268 — null = 전체 대상 (겹침 허용·전체 브랜드 등록이 열리며 null 응답이 실제로 발생)
        valueFormatter: ({ value }) => (value && value.length > 0 ? value.join(', ') : '전체'),
        tooltipField: 'partnerIds',
        tooltipComponent: ({ value }: { value: string[] | null }) => {
          const tooltipText = value && value.length > 0 ? value.join(', ') : '전체'

          return (
            <TooltipContent $isVisible={true} $type="bottom" $whiteSpace="nowrap">
              {tooltipText}
            </TooltipContent>
          )
        },
        cellClass: 'ellipsis-cell',
      },
      {
        field: 'brands',
        headerName: '브랜드',
        tooltipField: 'brands',
        tooltipComponent: ({ value }: { value: { brandName: string; brandId: string }[] | null }) => {
          const tooltipText = value && value.length > 0 ? value.map((brand) => `${brand.brandName}(${brand.brandId})`).join(', ') : '전체'

          return (
            <TooltipContent $isVisible={true} $type="bottom" $whiteSpace="nowrap">
              {tooltipText}
            </TooltipContent>
          )
        },
        width: 186,
        valueFormatter: ({ value }) =>
          value && value.length > 0
            ? value.map((brand: { brandName: string; brandId: string }) => `${brand.brandName}(${brand.brandId})`).join(', ')
            : '전체',
        cellClass: 'ellipsis-cell',
      },
      {
        // 시안 v4 — 카테고리 축. 빈 배열/미존재 = 전체
        field: 'categoryCodes',
        headerName: '카테고리',
        width: 200,
        valueFormatter: ({ value }) => (value && value.length > 0 ? value.map(categoryLabelOf).join(', ') : '전체'),
        tooltipField: 'categoryCodes',
        tooltipComponent: ({ value }: { value: string[] | null }) => {
          const tooltipText = value && value.length > 0 ? value.map(categoryLabelOf).join(', ') : '전체'

          return (
            <TooltipContent $isVisible={true} $type="bottom" $whiteSpace="nowrap">
              {tooltipText}
            </TooltipContent>
          )
        },
        cellClass: 'ellipsis-cell',
      },
      {
        // LOGISPRD-10268 — 할인 방식. 값 없음(구데이터/BE 미반영)은 국가 축과 같이 '-' (없는 값을 정률로 단정하지 않는다)
        field: 'discountType',
        headerName: '할인 방식',
        width: 90,
        valueFormatter: ({ value }) => (!value?.code ? '-' : value.code === 'FIXED' ? '정액' : '정률'),
      },
      {
        // LOGISPRD-10268 — 할인값: 일별 = 단일값(%/원), 기간 = 구간 요약
        field: 'discountValue',
        headerName: '할인값',
        width: 110,
        valueFormatter: ({ value, data }) => {
          if (data?.settlementCycle === 'MONTHLY') {
            return toTierSummaryText(data.tiers)
          }
          if (!value) return '-'
          // 할인값은 문자열이다 — 숫자로 바꾸면 정액 16자리가 반올림된다 (LOGISPRD-11361)
          return data?.discountType?.code === 'FIXED' ? `${toDiscountDisplay(value, false)}원` : `${value}%`
        },
        // LOGISPRD-11390 — 기간 프로모션은 셀에 `구간 N개` 만 들어간다. 진행중 건은 수정 버튼이 잠겨
        // 구간을 열어 볼 곳이 없었다 — hover 로 구간 전체를 편다. 구간이 없으면 undefined 로 툴팁을 끈다
        tooltipValueGetter: ({ data }) =>
          data?.settlementCycle === 'MONTHLY' && data.tiers && data.tiers.length > 0
            ? toTierTooltipText(data.tiers, data.discountType?.code === 'FIXED')
            : undefined,
        tooltipComponent: PromotionTierTooltip,
      },
      {
        // LOGISPRD-10268 — 정산 방식. 값 없음(구데이터/BE 미반영)은 국가 축과 같이 '-'
        field: 'settlementCycle',
        headerName: '정산 방식',
        width: 90,
        valueFormatter: ({ value }) => (!value ? '-' : value === 'MONTHLY' ? '기간' : '일별'),
      },
      {
        field: 'startDate',
        headerName: '프로모션 기간',
        width: 186,
        valueFormatter: ({ data }) => {
          const start = data?.startDate ? dayjs(data.startDate).format('YYYY-MM-DD') : ''
          const end = data?.endDate ? dayjs(data.endDate).format('YYYY-MM-DD') : ''
          return start && end ? `${start} ~ ${end}` : '-'
        },
      },
      {
        field: 'status.code',
        headerName: '상태',
        width: 80,
        cellRenderer: ({ value }: ICellRendererParams<PostPromotionListResDisplayType>) => {
          return <Badge type={value} />
        },
      },
      {
        // LOGISPRD-11394 — 비고 (최대 100자). 길면 셀은 말줄임, 전문은 툴팁
        field: 'remark',
        headerName: '비고',
        width: 150,
        valueFormatter: ({ value }) => value || '-',
        tooltipField: 'remark',
        // 비고는 100자까지라 한 줄로 두면 툴팁이 화면 밖으로 나간다 — 폭을 잡고 줄바꿈시킨다.
        // 여러 줄이 되므로 구간 툴팁(41-45행)과 같은 위치 보정을 같이 준다
        tooltipComponent: ({ value }: { value: string }) => {
          return (
            <TooltipContent
              $isVisible
              $type="bottom"
              $whiteSpace="pre-wrap"
              $width="360px"
              style={{ position: 'relative', bottom: 'unset', top: 10 }}
            >
              {value}
            </TooltipContent>
          )
        },
        cellClass: 'ellipsis-cell',
      },
      {
        field: 'createdBy',
        headerName: '등록자',
        width: 80,
      },
      {
        field: 'createdAt',
        cellDataType: 'date',
        headerName: '등록일',
        width: 100,
      },
      // MEMO: 수정자, 수정일에 경우 createdAt과 updatedAt이 같으면 수정 이력이 없는 걸로 판단, '-' 출력
      {
        field: 'updatedBy',
        headerName: '수정자',
        width: 80,
        valueFormatter: (params) => {
          return params.data?.updatedAt === params.data?.createdAt ? '-' : params.data?.updatedBy || '-'
        },
      },
      {
        field: 'updatedAt',
        headerName: '수정일',
        cellDataType: 'date',
        width: 100,
        valueFormatter: (params) => {
          return params.data?.updatedAt === params.data?.createdAt ? '-' : dayjs(params.data?.updatedAt).format(DATE_FORMAT.date)
        },
      },
      {
        field: 'editable',
        headerName: '수정',
        width: 74,
        cellRenderer: (params: ICellRendererParams<PostPromotionListResDisplayType>) => {
          const { data: selectedRow } = params
          return (
            <HStack style={{ width: '100%' }} justify="center">
              <Button
                kind="secondary"
                fit
                size="sm"
                disabled={selectedRow?.status.code !== 'PENDING' || selectedRow?.editable === false}
                onClick={(e) => {
                  // 렌더 시점에 잡힌 params.data 는 저장 후 갱신돼도 옛 행을 가리킨다 — 클릭 순간의 행을 다시 읽는다
                  const selectedRow = params.node.data
                  if (selectedRow === undefined) {
                    return
                  }

                  const editData: SettlementPromotionSchemaType & { promotionId: number } = {
                    promotionId: selectedRow.id,
                    serviceType: selectedRow.serviceType.code,
                    unitType: selectedRow.unitType ?? 'PCS', // LP8658 — 수정 프리필 시 unitType 유지 (BOX → PCS 깨짐 방지)
                    partnerIds: (selectedRow.partnerIds ?? [])[0] ?? '', // 전체 대상은 null 응답 — 가드 없으면 수정 클릭 시 크래시
                    brandIds: (selectedRow.brands ?? []).map((brand) => brand.brandId),
                    title: selectedRow.title,
                    // LOGISPRD-10268 — 신규 축 프리필 (BE 검색 응답 반영 전 구데이터 기본값: RATE·DAILY·KR)
                    country: (selectedRow.country?.code as PromotionCountry) ?? 'KR',
                    // 미전송 구간은 구 매핑으로 되읽는다 (한국=국내 · 일본=해외). 수정 화면에서 고칠 수 있다 (LOGISPRD-11448)
                    orderType:
                      (selectedRow.orderType?.code as PromotionOrderType) ?? (selectedRow.country?.code === 'JP' ? 'GLOBAL' : 'DOMESTIC'),
                    discountType: (selectedRow.discountType?.code as 'RATE' | 'FIXED') ?? 'RATE',
                    settlementCycle: selectedRow.settlementCycle ?? 'DAILY',
                    categoryCodes: selectedRow.categoryCodes ?? [],
                    tiers: (selectedRow.tiers ?? []).map((tier) => ({
                      fromQuantity: String(tier.fromValue),
                      toQuantity: tier.toValue === null ? '' : String(tier.toValue),
                      discountValue: tier.discountValue,
                    })),
                    discountValue: selectedRow.discountValue,
                    startDate: selectedRow.startDate,
                    endDate: selectedRow.endDate,
                    applyToAllPartners: false,
                    applyToAllBrands: false,
                    remark: selectedRow.remark ?? '',
                    // 상품 프로모션은 서버가 editable=false 로 내려 여기까지 오지 않는다 (LOGISPRD-13627)
                    skuIdsText: '',
                    // 저장된 축에서 종류를 되읽는다 (시안 v4 — 종류는 화면 전용, 서버 전송 없음)
                    promotionKind: toPromotionKind({
                      partnerIds: (selectedRow.partnerIds ?? [])[0] ?? '',
                      brandIds: (selectedRow.brands ?? []).map((brand) => brand.brandId),
                      categoryCodes: selectedRow.categoryCodes ?? [],
                    }),
                  }

                  setSelectedRowForEdit(editData)
                  promotionModal.onOpenModal()
                }}
              >
                수정
              </Button>
            </HStack>
          )
        },
      },
    ],
    [isInboundSettlementUnitTypeEnabled, categoryLabelOf],
  )

  return (
    <>
      <HStack full gap={16}>
        <StyledGridTable
          gridKey="BL02_HEAD"
          ref={$grid}
          title={
            <HStack gap={16}>
              <HStack gap={2} style={{ flex: '0 0 auto', color: colorSystem.text.common.tertiary }}>
                <Text kind="Body_14_bold" color={checkedNum === 0 ? colorSystem.text.common.tertiary : tokens.layerDesktop['fg-accent']}>
                  {checkedNum}
                </Text>
                <Text kind="Body_14_regular" color={checkedNum === 0 ? colorSystem.text.common.tertiary : colorSystem.text.common.primary}>
                  건 선택
                </Text>
              </HStack>
              <Button
                fit
                size="sm"
                kind="secondary"
                type="button"
                disabled={checkedNum === 0}
                onClick={() => {
                  terminatePromotionModal.onOpenModal()
                }}
              >
                프로모션 종료
              </Button>
            </HStack>
          }
          // 시안 v4 — '프로모션 등록' 왼쪽에 붙는 드롭다운 (엑셀 업로드 · 템플릿 다운로드)
          optionActionButton={{
            content: '프로모션 일괄 등록',
            kind: 'secondary',
            options: [
              { label: '엑셀 업로드', onClick: bulkUploadModal.onOpenModal },
              { label: '엑셀 템플릿 다운로드', onClick: () => onDownloadTemplate() },
            ],
          }}
          rowSelectable
          rowData={rowData}
          // 직전에 수정한 행이 남아 있으면 등록 모달이 그 값으로 채워진다 — 비우고 연다
          onAddRow={() => {
            setSelectedRowForEdit(null)
            promotionModal.onOpenModal()
          }}
          rowHeight={52}
          // 기본 2초 지연이면 구간을 보려고 셀 위에 멈춰 있어야 한다 (다른 정산 표와 같은 설정)
          tooltipShowDelay={0}
          gridOptions={gridOptions}
          addButtonName="프로모션 등록"
          isLoading={isLoading}
          isSearchVisible={false}
          isRowSelectable={(params) => {
            return params.data?.status.code !== 'ENDED'
          }}
          pagination
          onChangePage={onChangePage}
          currentPage={pageInfo.page || 0}
          countPerPage={pageInfo.size || GRID_SIZE.SETTLEMENT}
          totalCount={pageInfo.totalCount || 0}
          columnDefs={columnDefs}
        />
      </HStack>
      <PromotionFormModal
        open={promotionModal.open}
        // 제목이 같으면 컴포넌트가 재사용돼 이전 폼 값이 남는다 — 프로모션 id 로 구분한다 (신규는 new)
        key={selectedRowForEdit?.promotionId ?? 'new'}
        onClose={handleRegisterModalClose}
        defaultValues={selectedRowForEdit ? { ...selectedRowForEdit } : null}
        refetch={refetch}
      />
      {bulkUploadModal.open && <PromotionBulkUploadModal open={bulkUploadModal.open} onClose={bulkUploadModal.onCloseModal} refetch={refetch} />}
      {terminatePromotionModal.open && (
        <ConfirmModal
          title={`${checkedNum}건의 프로모션을 종료하시겠습니까?`}
          footer={
            <HStack gap={8}>
              <Button fit kind="secondary" onClick={terminatePromotionModal.onCloseModal}>
                취소
              </Button>
              <Button fit kind="primary_light" onClick={handleTerminatePromotion}>
                종료
              </Button>
            </HStack>
          }
        />
      )}
    </>
  )
}

const StyledGridTable = styled(GridTable<PostPromotionListResDisplayType>)`
  .ag-cell {
    display: flex;
    align-items: center;
  }

  .ellipsis-cell {
    display: block;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    line-height: 52px;
    padding: 0 8px;
  }
`
